import { addDays, format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { includes, toLower } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { replaceData, resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';
import { getMessage } from '@gef-ui/localization/messages';

import { EMPTY_VALUES } from 'constants/general';
import collapsibleTable from 'features/common/collapsibleTable';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { subheaders } from 'features/common/Subheader/constants';
import { getVisibleStateById } from 'features/common/Subheader/selectors';
import { setVisibleState } from 'features/common/Subheader/slice';
import { fetchLeadDetails, fetchLeadHistory } from 'features/views/leadDetail/actions';
import {
	dialogs,
	feedbackType,
	formDataFieldsMap,
	LEAD_ALREADY_PROCESSED,
	LEAD_ALREADY_TAKEN,
	LEAD_RETURNED_TO_MP_DUE_SLA_TIMEOUT,
	quickFilter,
} from 'features/views/marketPlace/constants';
import { getUser } from 'features/views/user/selectors';
import userContext from 'features/views/userContext';
import { cleanse, history, toIsoDateFormat, trimFormData } from 'utils';

import { fetchAvailableLeads as fetchAvailableLeadsOnDashboard } from '../dashboard/marketPlace/actions';
import * as types from './actionTypes';
import { FIELDS, MARKET_PLACE_FORM_NAME } from './form';
import {
	getOnDetailThroughMarketPlaceRow,
	getQuickFilterState,
	getSearchedWithCustomFilter,
	getSelectedDayOfNextDays,
	getSortValue,
} from './selectors';
import { requestFactory, requestFactoryInProgressAndAfterDeadline } from './utils';

export const reset = createAction(types.RESET);
export const saveLeads = createAction(types.SET_LEADS, (leads) => ({ leads }));
export const appendLeads = createAction(types.APPEND_LEADS, (leads) => ({ leads }));
export const resetLeads = createAction(types.RESET_LEADS);
export const setSortValue = createAction(types.SET_SORT_VALUE, (sortValue) => ({ sortValue }));
export const setSearchedWithCustomFilter = createAction(types.SET_SEARCHED_WITH_CUSTOM_FILTER, (value) => ({ value }));
export const setOnDetailThroughMarketPlaceRow = createAction(types.SET_ON_DETAIL_THROUGH_MARKETPLACE_ROW, (value) => ({
	value,
}));

export const resetFormCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(resetLeads());
		dispatch(setSortValue(EMPTY_VALUES.STRING));
		dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.leadsList));
		sendControlNext(MARKET_PLACE_FORM_NAME, resetForm());
	};

const processResponseOrShowDialog = (response) => (dispatch) => {
	const { data, pageMetaData } = response.body;

	if (!data || !Array.isArray(data)) return;

	if (isNilOrEmpty(data) && pageMetaData?.totalRows > 0) {
		return dispatch(dialog.actions.open(dialogs.tooManyResults));
	} else {
		dispatch(saveLeads(data));
	}
};

export const customSearchMarketPlaceLeads =
	(lastRequest) =>
	async (dispatch, getState, { getFormData }) => {
		const state = getState();
		const data = await getFormData(MARKET_PLACE_FORM_NAME);
		const formData = trimFormData(data);
		const contextUser = userContext.selectors.getContextUser(state);

		let finalBody;
		let finalQueryParams;

		if (lastRequest) {
			const { body, queryParams } = lastRequest;
			finalQueryParams = queryParams;
			finalBody = body;
			dispatch(setSortValue(queryParams?.sort));
		} else {
			finalQueryParams = { page: 0, size: 20, sort: EMPTY_VALUES.STRING };
			finalBody = requestFactory(formData);
			dispatch(setSortValue(EMPTY_VALUES.STRING));
		}

		dispatch(dialog.actions.clear());
		dispatch(
			fetch.actions.post(fetch.constants.urls.leadsList, {
				queryParams: finalQueryParams,
				body: finalBody,
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						pushToFilterDataLayer({
							filterSection: 'Příležitosti',
							formData: cleanse(formData),
							formDataFieldsMap,
						});
						dispatch(setSearchedWithCustomFilter(formData));
						dispatch(setOnDetailThroughMarketPlaceRow(false));
						dispatch(processResponseOrShowDialog(response));
					},
				],
			}),
		);
	};

export const reloadLeads = (sort) => (dispatch, getState) => {
	const { body, queryParams } = fetch.selectors.getLastRequest(fetch.constants.urls.leadsList)(getState());

	const finalQueryParams = {
		...queryParams,
		sort,
	};

	dispatch(dialog.actions.clear());
	dispatch(
		fetch.actions.post(fetch.constants.urls.leadsList, {
			queryParams: finalQueryParams,
			body,
			onSuccess: [
				(response) => () => {
					dispatch(processResponseOrShowDialog(response));
				},
			],
		}),
	);
};

export const sortLeads = (sort) => (dispatch) => {
	dispatch(setSortValue(sort));
	dispatch(reloadLeads(sort));
};

export const reloadInProgressLeads = () => (dispatch, getState) => {
	const contextUser = userContext.selectors.getContextUser(getState());
	const { body, queryParams } = fetch.selectors.getLastRequest(fetch.constants.urls.leadOverview)(getState());

	dispatch(
		fetch.actions.post(fetch.constants.urls.leadOverview, {
			queryParams,
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				(response) => () => {
					const body = response.body;
					dispatch(saveInProgressAndAfterDeadlineLeadOverview(body));
				},
			],
		}),
	);
};

export const fetchQuickFilterLeads = (quickFilterType) => (dispatch, getState) => {
	const contextUser = userContext.selectors.getContextUser(getState());
	const wasOnDetailThroughMarketPlaceRow = getOnDetailThroughMarketPlaceRow(getState());
	const selectedNextDay = getSelectedDayOfNextDays(getState());
	const body = {
		preset: quickFilterType,
		...(quickFilterType === quickFilter.FUTURE && {
			notificationDate: toIsoDateFormat(selectedNextDay),
		}),
	};

	const formData = {};
	switch (quickFilterType) {
		case quickFilter.CURRENT:
			formData.quickFilterCurrent = true;
			break;
		case quickFilter.FUTURE:
			formData.quickFilterFuture = true;
			break;
		case quickFilter.IN_PROGRESS:
			formData.quickFilterInProgress = true;
			break;
	}

	// to persist last page user was on when going back from detail - using onDetailThroughMarketPlaceRow value
	const { queryParams: lastQueryParams } = fetch.selectors.getLastRequest(fetch.constants.urls.leadsList)(getState());

	dispatch(dialog.actions.clear());
	dispatch(
		fetch.actions.post(fetch.constants.urls.leadsList, {
			queryParams: { page: wasOnDetailThroughMarketPlaceRow ? lastQueryParams?.page : 0, size: 20 },
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				(response) => () => {
					pushToFilterDataLayer({
						filterSection: 'Příležitosti',
						formData: cleanse(formData),
						formDataFieldsMap,
					});
					dispatch(setSearchedWithCustomFilter(false));
					dispatch(setOnDetailThroughMarketPlaceRow(false));
					dispatch(processResponseOrShowDialog(response));
				},
			],
		}),
	);
};

export const fetchAvailableMarketLeads = () => (dispatch, getState) => {
	const contextUser = userContext.selectors.getContextUser(getState());
	const userLogin = getUser(getState());

	dispatch(
		fetch.actions.get(fetch.constants.urls.competitionAvailableLeads, {
			queryParams: { userLogin: contextUser ? contextUser.login : userLogin },
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				(response) => () => {
					const body = response.body;
					dispatch(saveCompetitionAvailableLeads(body));
				},
			],
		}),
	);
};

export const takeLead = (leadId) => (dispatch, getState) => {
	const contextUser = userContext.selectors.getContextUser(getState());
	const userLogin = getUser(getState());
	const finalLogin = contextUser ? contextUser.login : userLogin;
	const quickFilterState = getQuickFilterState(getState());

	return dispatch(
		fetch.actions.get(fetch.constants.urls.takeLead, {
			replaceParams: {
				id: leadId,
			},
			queryParams: { destinationQueueGroup: toLower(finalLogin) },
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				() => () => {
					enqueueSnackbar(getMessage('toastr:marketPlaceSuccessfulyTakeLead'), { variant: 'success' });
					dispatch(fetchAvailableMarketLeads());
					dispatch(loadWithQuickFilter(quickFilterState));
				},
			],
			onError: [
				(error) => () => {
					if (error?.failure?.parsedContent?.code === LEAD_ALREADY_TAKEN) {
						if (window.location.pathname === '/dashboard') {
							dispatch(fetchAvailableLeadsOnDashboard());
						}

						if (window.location.pathname === '/market-place') {
							dispatch(fetchAvailableMarketLeads());
							dispatch(resetOrLoadUpMarketPlaceFilter());
						}

						enqueueSnackbar(getMessage('toastr:marketPlaceLeadAlreadyTaken'), { variant: 'error' });
					}
				},
			],
			shouldReportError: (error) => {
				return error?.failure?.parsedContent?.code !== LEAD_ALREADY_TAKEN;
			},
		}),
	);
};

export const changeQuickFilter = createAction(types.CHANGE_QUICK_FILTER, (type) => ({ type }));
export const saveCompetitionAvailableLeads = createAction(types.SET_AVAILABLE_LEADS, (competitionAvailableLeads) => ({
	competitionAvailableLeads,
}));
export const changeSelectedDayOfNextDay = createAction(types.CHANGE_SELECTED_DAY_OF_NEXT_DAYS, (selectedNextDay) => ({
	selectedNextDay,
}));
export const saveInProgressLeadsByType = createAction(types.SET_INPROGRESS_LEADS_BY_TYPE, (taskTypeId, leads) => ({
	taskTypeId,
	leads,
}));
export const appendInProgressLeadsByType = createAction(types.APPEND_INPROGRESS_LEADS_BY_TYPE, (taskTypeId, leads) => ({
	taskTypeId,
	leads,
}));
export const setSelectedInProgressTaskTypeId = createAction(types.SET_SELECTED_INPROGRESS_TASKTYPEID, (taskTypeId) => ({
	taskTypeId,
}));

export const loadWithQuickFilter = (type) => (dispatch) => {
	dispatch(setVisibleState({ id: subheaders.marketPlace, visible: false }));

	// in case of inProgress quickfilter - firstly, we only fetch the overview
	if (type === quickFilter.INPROGRESSORAFTERDEADLINE) {
		dispatch(fetchInProgressOrAfterDeadlineLeadOverview());
	} else {
		dispatch(fetchQuickFilterLeads(type));
	}
};

export const resetOrLoadUpMarketPlaceFilter = () => (dispatch, getState) => {
	const quickFilterState = getQuickFilterState(getState());
	const visible = getVisibleStateById(getState(), subheaders.marketPlace);

	if (visible) {
		dispatch(resetOrRefetchCustomFilter());
	} else {
		dispatch(collapsibleTable.actions.removeOpenedRows('MarketPlaceLeadsTable'));
		dispatch(loadWithQuickFilter(quickFilterState));
	}
};

export const resetNextSevenDaysPills = () => (dispatch) => {
	const firstDayInPills = format(addDays(new Date(), 1), 'yyyy-MM-dd');
	dispatch(changeSelectedDayOfNextDay(firstDayInPills));
};

export const saveInProgressAndAfterDeadlineLeadOverview = createAction(
	types.SET_INPROGRESS_AND_DEADLINE_LEAD_OVERVIEW,
	(inProgressAndAfterDeadlineLeadOverview) => ({
		inProgressAndAfterDeadlineLeadOverview,
	}),
);

export const fetchInProgressOrAfterDeadlineLeadOverview = () => (dispatch, getState) => {
	const contextUser = userContext.selectors.getContextUser(getState());
	const userLogin = getUser(getState());
	const finalLogin = contextUser ? contextUser.login : userLogin;

	const body = requestFactoryInProgressAndAfterDeadline({ login: finalLogin });

	dispatch(
		fetch.actions.post(fetch.constants.urls.leadOverview, {
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				(response) => () => {
					const body = response.body;
					dispatch(saveInProgressAndAfterDeadlineLeadOverview(body));
					dispatch(setSearchedWithCustomFilter(false));
				},
			],
		}),
	);
};

export const fetchInProgressAndAfterDeadLineLeadsByType = (taskTypeId) => (dispatch, getState) => {
	const contextUser = userContext.selectors.getContextUser(getState());
	const body = {
		preset: 'TASK_TYPE_DETAIL',
		taskTypeId,
	};

	dispatch(
		fetch.actions.post(fetch.constants.urls.leadsList, {
			uniqueReqID: `inProgressAndAfterDeadline.${taskTypeId}`,
			queryParams: { page: 0, size: 10 },
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				(response) => () => {
					const body = response.body;
					dispatch(saveInProgressLeadsByType(taskTypeId, body.data));
				},
			],
		}),
	);
};

export const resetOrRefetchCustomFilter =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const lastInsertedCustomFilter = getSearchedWithCustomFilter(getState());

		// when returning from custom filter searched detail, prefill form and custom refetch with lastRequest data
		if (lastInsertedCustomFilter) {
			sendControlNext(MARKET_PLACE_FORM_NAME, replaceData({ data: lastInsertedCustomFilter }));
			const lastRequest = fetch.selectors.getLastRequest(fetch.constants.urls.leadsList)(getState());
			dispatch(customSearchMarketPlaceLeads(lastRequest));
		} else {
			// when changing from quickFilter to customFilter > only reset leads
			dispatch(resetLeads());
			dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.leadsList));
		}
	};

export const changeLeadFeedback = (leadId, newFeedbackId, type) => (dispatch, getState) => {
	const sortValue = getSortValue(getState());

	dispatch(
		fetch.actions.post(fetch.constants.urls.finishLead, {
			uniqueReqID: leadId,
			replaceParams: { id: leadId },
			body: {
				finishTypeId: newFeedbackId,
			},
			onSuccess: [
				() => () => {
					enqueueSnackbar(getMessage('toastr:marketPlaceLeadFeedbackProcessedSuccessfully'), {
						variant: 'success',
					});
					if (type === feedbackType.IN_DETAIL) {
						dispatch(fetchLeadDetails(leadId));
						dispatch(fetchLeadHistory(leadId));
					} else {
						dispatch(collapsibleTable.actions.removeOpenedRows('MarketPlaceLeadsTable'));
						dispatch(reloadLeads(sortValue));
					}
				},
			],
			onError: [
				({ failure }) =>
					() => {
						if (failure?.parsedContent?.code === LEAD_RETURNED_TO_MP_DUE_SLA_TIMEOUT) {
							if (window.location.pathname === `/lead/${leadId}/detail`) {
								history.go(-1);
							}

							if (window.location.pathname === '/market-place') {
								dispatch(fetchAvailableMarketLeads());
								dispatch(resetOrLoadUpMarketPlaceFilter());
							}

							enqueueSnackbar(getMessage('toastr:marketPlaceLeadAlreadyTaken'), { variant: 'error' });
						}

						if (failure?.parsedContent?.code === LEAD_ALREADY_PROCESSED) {
							enqueueSnackbar(getMessage('toastr:marketPlaceLeadAlreadyProcessed'), { variant: 'error' });

							if (type === feedbackType.IN_DETAIL) {
								dispatch(fetchLeadDetails(leadId));
								dispatch(fetchLeadHistory(leadId));
							} else {
								dispatch(collapsibleTable.actions.removeOpenedRows('MarketPlaceLeadsTable'));
								dispatch(reloadLeads(sortValue));
							}
						}
					},
			],
			shouldReportError: ({ failure }) => {
				return !includes(failure?.parsedContent?.code, [
					LEAD_RETURNED_TO_MP_DUE_SLA_TIMEOUT,
					LEAD_ALREADY_PROCESSED,
				]);
			},
		}),
	);
};

export const onTaskTypeChange =
	(taskSubtypeOptions) =>
	(dispatch, getState, { sendControlNext }) => {
		if (taskSubtypeOptions.length === 1) {
			const onlyOption = taskSubtypeOptions[0].code;
			sendControlNext(MARKET_PLACE_FORM_NAME, setValue({ fieldName: FIELDS.taskSubTypeId, value: onlyOption }));
		} else {
			sendControlNext(MARKET_PLACE_FORM_NAME, setValue({ fieldName: FIELDS.taskSubTypeId, value: null }));
		}
	};

// custom pagination only for inProgressAndAfterDeadline leads
export const loadMore =
	({ fetchStorePath, taskTypeId }) =>
	(dispatch, getState) => {
		const contextUser = userContext.selectors.getContextUser(getState());
		const { queryParams, body } = fetch.selectors.getLastRequest(fetchStorePath)(getState());
		const updatedQueryParams = {
			...queryParams,
			page: queryParams.page + 1,
		};

		dispatch(
			fetch.actions.post(fetch.constants.urls.leadsList, {
				uniqueReqID: `inProgressAndAfterDeadline.${taskTypeId}`,
				queryParams: updatedQueryParams,
				body,
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						if (!response.body) return;
						const { data } = response.body;
						dispatch(appendInProgressLeadsByType(taskTypeId, data));
					},
				],
			}),
		);
	};

export const loadPage =
	({ fetchStorePath, taskTypeId, page }) =>
	(dispatch, getState) => {
		const contextUser = userContext.selectors.getContextUser(getState());
		const { queryParams, body } = fetch.selectors.getLastRequest(fetchStorePath)(getState());
		const updatedQueryParams = {
			...queryParams,
			page,
		};

		dispatch(
			fetch.actions.post(fetch.constants.urls.leadsList, {
				uniqueReqID: `inProgressAndAfterDeadline.${taskTypeId}`,
				queryParams: updatedQueryParams,
				body,
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						if (!response.body) return;
						const { data } = response.body;
						dispatch(saveInProgressLeadsByType(taskTypeId, data));
					},
				],
			}),
		);
	};

// quickFilter pagination
export const loadMoreQuickFilter = () => (dispatch, getState) => {
	const contextUser = userContext.selectors.getContextUser(getState());
	const { queryParams, body } = fetch.selectors.getLastRequest(fetch.constants.urls.leadsList)(getState());
	const updatedQueryParams = {
		...queryParams,
		page: queryParams.page + 1,
	};

	dispatch(
		fetch.actions.post(fetch.constants.urls.leadsList, {
			queryParams: updatedQueryParams,
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					const { data } = response.body;
					dispatch(appendLeads(data));
					dispatch(setSearchedWithCustomFilter(false));
				},
			],
		}),
	);
};

export const loadPageQuickFilter = (page) => (dispatch, getState) => {
	const contextUser = userContext.selectors.getContextUser(getState());
	const { queryParams, body } = fetch.selectors.getLastRequest(fetch.constants.urls.leadsList)(getState());
	const updatedQueryParams = {
		...queryParams,
		page,
	};
	dispatch(
		fetch.actions.post(fetch.constants.urls.leadsList, {
			queryParams: updatedQueryParams,
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					const { data } = response.body;
					dispatch(saveLeads(data));
					dispatch(setSearchedWithCustomFilter(false));
				},
			],
		}),
	);
};
